import { Search, X } from "lucide-react"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"

interface DeviceSearchBarProps {
  /** Live value of the input, before the user commits it. */
  value: string
  onChange: (value: string) => void
  /** Commit the current value as the active search (button click / Enter). */
  onSubmit: () => void
  /** Clear both the input and the committed search. */
  onClear: () => void
  /** Committed search term, shown as a badge so the user sees what is being searched. */
  activeFilter: string
  placeholder?: string
  disabled?: boolean
}

// Search is committed explicitly (button or Enter) so we don't hit the API on every
// keystroke. The committed term is surfaced as a badge and cleared via either "x".
export function DeviceSearchBar({
  value,
  onChange,
  onSubmit,
  onClear,
  activeFilter,
  placeholder = "Search devices by ID, client, venue, area...",
  disabled = false,
}: DeviceSearchBarProps) {
  return (
    <div className="space-y-2" data-tour="search-devices">
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-lg">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={placeholder}
            value={value}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault()
                onSubmit()
              }
            }}
            className="h-11 pl-10 pr-10 bg-background border-border text-foreground placeholder:text-muted-foreground"
          />
          {(value || activeFilter) && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onClear}
              disabled={disabled}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 text-muted-foreground hover:text-foreground hover:bg-secondary"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
        <Button
          onClick={onSubmit}
          disabled={disabled}
          className="h-11 bg-primary hover:bg-primary/90 text-primary-foreground"
        >
          <Search className="h-4 w-4 mr-2" />
          Search
        </Button>
      </div>

      {activeFilter && (
        <div className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">Searching for</span>
          <Badge variant="secondary" className="gap-1 pr-1">
            <span className="max-w-[16rem] truncate">{activeFilter}</span>
            <button
              type="button"
              onClick={onClear}
              disabled={disabled}
              aria-label="Clear search"
              className="rounded-full p-0.5 hover:bg-background/60"
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        </div>
      )}
    </div>
  )
}
